import React, { useEffect, useState, useCallback } from 'react';
import { MessageCircle, Phone } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Select from '../../components/common/Select';
import Badge from '../../components/common/Badge';
import EmptyState from '../../components/common/EmptyState';
import Skeleton from '../../components/common/Skeleton';
import { leadService } from '../../services/leadService';
import { useToast } from '../../contexts/ToastContext';
import { formatRelativeTime, formatDateTime } from '../../utils/formatRelativeTime';

const STATUS = {
  new: { label: 'Novo', color: 'info' },
  contacted: { label: 'Em contato', color: 'warning' },
  closed: { label: 'Fechado', color: 'success' },
};

const LeadsManagement = () => {
  const { error, success } = useToast();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await leadService.getAll({ status: filterStatus || undefined, limit: 100 });
      setLeads(data.rows || []);
    } catch (e) {
      error(e.message || 'Erro ao carregar leads');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, error]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, newStatus) => {
    try {
      await leadService.updateStatus(id, newStatus);
      success(`Lead movido para ${STATUS[newStatus].label}`);
      load();
    } catch (e) {
      error(e.message || 'Erro ao atualizar lead');
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-app-text">Leads do WhatsApp</h1>
          <p className="text-sm text-app-text-secondary mt-1">
            Clientes que demonstraram interesse nos seus produtos.
          </p>
        </div>
        <div className="w-48">
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            options={[
              { value: '', label: 'Todos status' },
              { value: 'new', label: 'Novos' },
              { value: 'contacted', label: 'Em contato' },
              { value: 'closed', label: 'Fechados' },
            ]}
          />
        </div>
      </div>

      {loading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="block" height={140} />
          ))}
        </div>
      )}

      {!loading && leads.length === 0 && (
        <Card>
          <EmptyState
            icon={MessageCircle}
            title="Nenhum lead por aqui ainda"
            description="Compartilhe seu catálogo pra começar a receber interesses no WhatsApp."
          />
        </Card>
      )}

      <ul className="space-y-3">
        {leads.map((lead) => {
          const s = STATUS[lead.status] || { label: lead.status, color: 'neutral' };
          const phoneDigits = (lead.customerPhone || '').replace(/\D/g, '');
          const waUrl = phoneDigits ? `https://wa.me/${phoneDigits}` : null;
          return (
            <li key={lead.id}>
              <Card className="p-4">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-app-text">{lead.customerName}</span>
                      <Badge color={s.color} variant="soft" size="sm">{s.label}</Badge>
                    </div>
                    <p className="text-sm text-app-text-secondary">
                      {lead.customerPhone}
                      {lead.customerEmail ? ` · ${lead.customerEmail}` : ''}
                    </p>
                    {(lead.items || []).length > 0 && (
                      <ul className="text-sm mt-2 text-app-text space-y-0.5">
                        {lead.items.map((it, i) => (
                          <li key={i}>
                            <span className="font-medium">{it.quantity}×</span> {it.productName}
                            {it.variationLabel ? ` (${it.variationLabel})` : ''}
                          </li>
                        ))}
                      </ul>
                    )}
                    {lead.message && (
                      <p className="text-sm mt-2 italic text-app-text-secondary">
                        "{lead.message}"
                      </p>
                    )}
                    <p className="text-xs text-app-text-secondary mt-2">
                      {formatRelativeTime(lead.createdAt)} · {formatDateTime(lead.createdAt)}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 md:w-56">
                    {waUrl && (
                      <a href={waUrl} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" fullWidth leftIcon={<Phone size={14} />} variant="accent">
                          Abrir WhatsApp
                        </Button>
                      </a>
                    )}
                    {lead.status !== 'contacted' && (
                      <Button size="sm" variant="subtle" fullWidth onClick={() => updateStatus(lead.id, 'contacted')}>
                        Marcar em contato
                      </Button>
                    )}
                    {lead.status !== 'closed' && (
                      <Button size="sm" fullWidth onClick={() => updateStatus(lead.id, 'closed')}>
                        Fechar lead
                      </Button>
                    )}
                    {lead.status === 'closed' && (
                      <Button size="sm" variant="subtle" fullWidth onClick={() => updateStatus(lead.id, 'new')}>
                        Reabrir
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default LeadsManagement;
